import { isValidPhoneNumber } from "react-phone-number-input";
import {
  APPLICANT_TYPES,
  BENEFICIAL_OWNER_MIN_SHARE,
  MAX_BENEFICIAL_OWNERS,
  emptyKyb,
  emptyKyc,
  emptyPerson,
} from "../constants/kyc";

/**
 * Checks for the verification forms, mirroring the server's so a mistake is marked
 * before submitting. The server still decides, and its errors come back keyed by the
 * same field paths ("director.email", "beneficialOwners.1.sharePercent").
 *
 * @see mintly-back/src/services/kyc
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GEORGIAN_ID_CODE = /^(\d{9}|\d{11})$/;
const PERSONAL_ID = /^[A-Z0-9]{5,20}$/;
const GEORGIAN_IBAN = /^GE\d{2}[A-Z]{2}\d{16}$/;

const str = (v) => String(v ?? "").trim();
const compact = (v) => str(v).replace(/\s+/g, "").toUpperCase();

/**
 * The verification form's parts, for each kind of owner — the create-business wizard
 * shows one per step. An individual's fits on one.
 */
export const VERIFICATION_STEPS = {
  individual: ["details"],
  company: ["company", "activity", "people"],
};

/** Which step a field lives on, so an error can send someone back to it. */
export const verificationStepOf = (applicantType, path) => {
  if (applicantType === "individual") return "details";
  if (path.startsWith("businessActivit")) return "activity";
  if (path.startsWith("director") || path.startsWith("beneficialOwners") || path === "confirmed") {
    return "people";
  }
  return "company";
};

function checkActivities(errors, form, t) {
  if (!form.businessActivities?.length) {
    errors.businessActivities = t("kyc.errors.activities");
  } else if (form.businessActivities.includes("other") && !str(form.businessActivityOther)) {
    errors.businessActivityOther = t("kyc.errors.activityOther");
  }
}

function checkEmail(errors, path, value, t) {
  if (!str(value)) errors[path] = t("kyc.errors.required");
  else if (!EMAIL.test(str(value))) errors[path] = t("kyc.errors.email");
}

function checkPhone(errors, path, value, t) {
  if (!str(value)) errors[path] = t("kyc.errors.required");
  else if (!isValidPhoneNumber(str(value))) errors[path] = t("kyc.errors.phone");
}

function checkPersonalId(errors, path, value, t) {
  if (!compact(value)) errors[path] = t("kyc.errors.required");
  else if (!PERSONAL_ID.test(compact(value))) errors[path] = t("kyc.errors.personalId");
}

function checkPerson(errors, prefix, p, t, withShare) {
  if (!str(p.fullName)) errors[`${prefix}.fullName`] = t("kyc.errors.required");
  checkPersonalId(errors, `${prefix}.personalId`, p.personalId, t);
  if (!str(p.citizenship)) errors[`${prefix}.citizenship`] = t("kyc.errors.required");
  if (typeof p.pep !== "boolean") errors[`${prefix}.pep`] = t("kyc.errors.yesNo");
  checkEmail(errors, `${prefix}.email`, p.email, t);
  checkPhone(errors, `${prefix}.phone`, p.phone, t);
  if (withShare) {
    const share = Number(str(p.sharePercent).replace(",", "."));
    if (!str(p.sharePercent) || !Number.isFinite(share)) {
      errors[`${prefix}.sharePercent`] = t("kyc.errors.required");
    } else if (share < BENEFICIAL_OWNER_MIN_SHARE || share > 100) {
      errors[`${prefix}.sharePercent`] = t("kyc.errors.share", { min: BENEFICIAL_OWNER_MIN_SHARE });
    }
  }
}

/**
 * A company's form, or just the given steps of it.
 * @returns {Record<string, string>} errors by field path; empty when it's fine
 */
function validateCompany(form, t, steps = VERIFICATION_STEPS.company) {
  const errors = {};
  const on = (step) => steps.includes(step);

  if (on("company")) {
    if (!str(form.companyName)) errors.companyName = t("kyc.errors.required");
    if (!str(form.brandName)) errors.brandName = t("kyc.errors.required");
    if (!form.legalType) errors.legalType = t("kyc.errors.select");
    else if (form.legalType === "other" && !str(form.legalTypeOther)) {
      errors.legalTypeOther = t("kyc.errors.required");
    }
    if (!compact(form.identificationCode)) errors.identificationCode = t("kyc.errors.required");
    else if (!GEORGIAN_ID_CODE.test(compact(form.identificationCode))) {
      errors.identificationCode = t("kyc.errors.identificationCode");
    }
    if (str(form.website) && !/^(https?:\/\/)?[^\s/]+\.[^\s]+$/i.test(str(form.website))) {
      errors.website = t("kyc.errors.website");
    }
    if (!compact(form.iban)) errors.iban = t("kyc.errors.required");
    else if (!GEORGIAN_IBAN.test(compact(form.iban))) errors.iban = t("kyc.errors.iban");
    if (typeof form.acceptsForeignCards !== "boolean") {
      errors.acceptsForeignCards = t("kyc.errors.yesNo");
    }
    if (!form.annualTurnover) errors.annualTurnover = t("kyc.errors.select");
  }

  if (on("activity")) checkActivities(errors, form, t);

  if (on("people")) {
    checkPerson(errors, "director", form.director || {}, t, false);
    const owners = form.beneficialOwners || [];
    if (!owners.length) errors.beneficialOwners = t("kyc.errors.owners");
    owners.forEach((o, i) => checkPerson(errors, `beneficialOwners.${i}`, o, t, true));
    const total = owners.reduce(
      (sum, o) => sum + (Number(str(o.sharePercent).replace(",", ".")) || 0),
      0
    );
    if (owners.length > MAX_BENEFICIAL_OWNERS || total > 100) {
      errors.beneficialOwners = t("kyc.errors.sharesTotal");
    }
    if (form.confirmed !== true) errors.confirmed = t("kyc.errors.confirm");
  }

  return errors;
}

/** An individual's form: name, personal number, business activity, email and phone. */
function validateIndividual(form, t) {
  const errors = {};
  if (!str(form.fullName)) errors.fullName = t("kyc.errors.required");
  checkPersonalId(errors, "personalId", form.personalId, t);
  checkActivities(errors, form, t);
  checkEmail(errors, "email", form.email, t);
  checkPhone(errors, "phone", form.phone, t);
  return errors;
}

/**
 * A business's form, as the kind its owner chose — a company's can be checked a few
 * steps at a time. Nothing chosen yet is the one error.
 * @returns {Record<string, string>} errors by field path; empty when it's fine
 */
export function validateVerification(form, t, steps) {
  if (form.applicantType === "individual") return validateIndividual(form, t);
  if (form.applicantType === "company") return validateCompany(form, t, steps);
  return { applicantType: t("kyc.errors.applicantType") };
}

/** The phone input only takes international numbers (+995…); anything else starts blank. */
const internationalPhone = (value) => {
  const v = String(value || "").replace(/[\s()-]/g, "");
  return /^\+\d{6,15}$/.test(v) ? v : "";
};

const person = (p, withShare) => {
  const base = emptyPerson(withShare);
  if (!p) return base;
  return {
    ...base,
    fullName: p.fullName || "",
    personalId: p.personalId || "",
    citizenship: p.citizenship || "",
    pep: typeof p.pep === "boolean" ? p.pep : null,
    email: p.email || "",
    phone: internationalPhone(p.phone),
    ...(withShare
      ? { sharePercent: p.sharePercent === null || p.sharePercent === undefined ? "" : String(p.sharePercent) }
      : {}),
  };
};

/** A company's answers, from a submitted application or a prefill. The tick is never carried over. */
function kybToForm(data) {
  const form = emptyKyb();
  if (!data) return form;
  for (const key of Object.keys(form)) {
    if (key === "director" || key === "beneficialOwners" || key === "confirmed") continue;
    if (data[key] !== undefined && data[key] !== null) form[key] = data[key];
  }
  form.director = person(data.director, false);
  form.beneficialOwners = data.beneficialOwners?.length
    ? data.beneficialOwners.map((o) => person(o, true))
    : [emptyPerson(true)];
  return form;
}

/** An individual's answers, from a submitted application or a prefill. */
function kycToForm(data) {
  const form = emptyKyc();
  if (!data) return form;
  for (const key of Object.keys(form)) {
    if (data[key] !== undefined && data[key] !== null) form[key] = data[key];
  }
  form.phone = internationalPhone(form.phone);
  return form;
}

/**
 * A business's form, filled from a submitted application or a prefill — or from an
 * application spread over a prefill, so the kind it didn't answer starts from the
 * prefill should the owner switch.
 */
export function verificationToForm(data) {
  return {
    ...kybToForm(data),
    ...kycToForm(data),
    applicantType: APPLICANT_TYPES.includes(data?.applicantType) ? data.applicantType : "",
  };
}

/** What's sent: the chosen kind's answers — the other kind's, prefilled or not, stay here. */
export function verificationPayload(form) {
  const keys = Object.keys(form.applicantType === "individual" ? emptyKyc() : emptyKyb());
  return {
    applicantType: form.applicantType,
    ...Object.fromEntries(keys.map((key) => [key, form[key]])),
  };
}

/** A copy of `obj` with the value at a dotted path ("beneficialOwners.1.email") replaced. */
export function setIn(obj, path, value) {
  const [head, ...rest] = path.split(".");
  const copy = Array.isArray(obj) ? [...obj] : { ...obj };
  copy[head] = rest.length ? setIn(obj?.[head] ?? {}, rest.join("."), value) : value;
  return copy;
}

/**
 * Errors without the ones a change at `path` may have fixed: that field, anything
 * under it, and anything it's part of — editing one share can fix the total.
 */
export function clearErrorsAt(errors, path) {
  const next = {};
  for (const [key, message] of Object.entries(errors || {})) {
    const related = key === path || key.startsWith(`${path}.`) || path.startsWith(`${key}.`);
    if (!related) next[key] = message;
  }
  return next;
}

/** Field errors from a failed request, whichever way the API client passed them on. */
export const serverFieldErrors = (err) => err?.response?.data?.errors || err?.errors || null;

/** A message from a failed request, whichever way the API client passed it on. */
export const serverMessage = (err) => err?.response?.data?.message || err?.message || "";
